/* Core & libraries */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import moment from 'helpers/moment';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/pt';
import { useTheme } from "react-jss";

/* Helpers, constants, custom methods, internal libraries */
import { nameInitials } from "../../../../helpers/general";
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { AIRLINES } from 'constants/appConstants';
import { useUserContext } from 'contexts/userContext';

/* Components */
import { ReactComponent as CalendarIcon } from '../../../../resources/images/status-icon-calendar.svg';
import { ReactComponent as CanceledIcon } from "../../../../resources/images/close-icon.svg";
import { ReactComponent as DelayedIcon } from "../../../../resources/images/status-icon-delayed.svg";
import { ReactComponent as OnTimeIcon } from "../../../../resources/images/status-icon-ontime.svg";
import { ReactComponent as DivertedIcon } from "../../../../resources/images/status-icon-diverted.svg";
import { ReactComponent as ArrivedIcon } from "../../../../resources/images/check-icon.svg";
import { ReactComponent as NotAvailable } from "../../../../resources/images/not-available.svg";
import { ReactComponent as PlusSign } from '../../../../resources/images/plus.svg';

import styles from "./styles.js"

export const AirTypeColumns = ({ globalLocale, displayBookingInfoModal, displayInviteGuestModal }) => {
  const { checkPermission } = useHasPermission();
  const theme = useTheme();
  const classes = styles({ theme });
  const userContext = useUserContext();
  const providerCode = userContext?.userInfo?.providerInfo?.providerCode;
  const providerIsNotAlaska = providerCode !== AIRLINES.AS;

  const getStatus = (type) => {
    switch (type?.toUpperCase()) {
      case 'SCHEDULED':
        return (
          <div className={classes['scheduled']}>
            <CalendarIcon className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.scheduled'} />
          </div>
        );
      case 'ONTIME':
        return (
          <div className={classes['ontime']}>
            <OnTimeIcon className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.ontime'} />
          </div>
        );
      case 'DELAYED':
        return (
          <div className={classes['delayed']}>
            <DelayedIcon className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.delayed'} />
          </div>
        );
      case 'DIVERTED':
        return (
          <div className={classes['diverted']}>
            <DivertedIcon className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.diverted'} />
          </div>
        );
      case 'CANCELED':
        return (
          <div className={classes['canceled']}>
            <CanceledIcon className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.canceled'} />
          </div>
        );
      case 'ARRIVED':
        return (
          <div className={classes['arrived']}>
            <ArrivedIcon className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.arrived'} />
          </div>
        );
      case 'STATUS UNAVAILABLE':
        return (
          <div className={classes['notavailable']}>
            <NotAvailable className={classes.statusIcon} />
            <FormattedMessage id={'safetyGrid.notAvailable'} />
          </div>
        );
      default:
        return null;
    }
  };

  return {
    startDate: (row) => (
      <div className={classes.departureStatus} onClick={() => displayBookingInfoModal(row)}>
        <div className={classes.startDate}>{moment(row.startDate).locale(globalLocale).format('D MMM YYYY')}</div>
        <div className={classes.startTime}>{moment(row.startDate).format('h:mm A')}</div>
      </div>
    ),

    departureStatus: (row) => (
      <div className={classes.departureStatus} onClick={() => displayBookingInfoModal(row)}>
        {getStatus(row.departureStatus)}
      </div>
    ),

    mainStatus: (row) => (
      <div onClick={() => displayBookingInfoModal(row)}>
        {checkPermission('F_VIEW_FLIGHT_DETAILS') ? getStatus(row.mainStatus) : getStatus('Scheduled')}
      </div>
    ),

    avatarName: (row) => {
      const name = `${row.firstName} ${row.lastName}`;
      
      return (
        <Link onClick={() => displayBookingInfoModal(row)} className={classes.link}>
          <div className={classes.nameWrapper} tabIndex={0}>
            <div className={classes.initialsAvatar}>
              <span>{nameInitials(name)}</span>
            </div>

            <div className={classes.details}>
              <div className={classes.detailsWrapper}>
                <span className={classes.name}>{name}</span>

                {row?.guestBooking && (
                  <span className={classes.badge} {...(providerIsNotAlaska && { onClick: (e) => displayInviteGuestModal(e, name) })}>
                    <span className={classes.badgeText}>
                      <FormattedMessage id="safetyGrid.guest" />
                    </span>
                    {providerIsNotAlaska && <PlusSign />}
                  </span>
                )}
              </div>

              <p>{row?.emailAddress || '-'}</p>
            </div>
          </div>
        </Link>
      );
    },
  };
};